import { Link, useLocation, useParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { SponsorConfig } from '../shared/tokens/sponsor-config.types'
import { useAuth } from '../contexts/AuthContext'
import NDAGate from '../components/gates/NDAGate'
import NotFound from './NotFound'

const REPORT_FALLBACK_NAMES: Record<string, string> = {
  master: 'Yan-Vada Master Pitch',
}

interface SponsorValueReportPageProps {
  defaultSlug?: string
  publicCopy?: boolean
  publicProspect?: boolean
}

function temporaryReportSrc(slug: string, hash: string) {
  if (slug === 'the-licenced') return `/sponsor-reports/value-report.html${hash}`
  return `/sponsor-reports/value-report.html${hash}`
}

export default function SponsorValueReportPage({ defaultSlug, publicCopy = false, publicProspect = false }: SponsorValueReportPageProps = {}) {
  const params = useParams<{ slug: string }>()
  const slug = params.slug ?? defaultSlug ?? 'master'
  const location = useLocation()
  const { isLoggedIn, acceptedNdaSponsors, loading, rights, status } = useAuth()
  const [config, setConfig] = useState<SponsorConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const frameRef = useRef<HTMLIFrameElement | null>(null)
  const ndaAcceptanceKey = acceptedNdaSponsors.join('|')
  const referenceAccess = rights['pitch.analytics_references'] === true
  const isMasterReport = slug === 'master'
  const hasAcceptedReportNda = acceptedNdaSponsors.includes(slug)
  const bypassesReportNda = rights['pitch.bypass_nda'] === true
  const reportSrc = useMemo(() => {
    if (publicCopy || publicProspect) return temporaryReportSrc(slug, location.hash)
    return `/sponsor-reports/value-report.html${location.hash}`
  }, [location.hash, publicCopy, publicProspect, slug])
  const reportAnchor = location.hash.replace(/^#/, '')
  const sendReportContext = useCallback(() => {
    const target = frameRef.current?.contentWindow
    if (!target) return
    target.postMessage({
      type: 'yan-vada:sponsor-report-context',
      analyticsReferences: referenceAccess,
      anchor: reportAnchor,
    }, window.location.origin)
  }, [referenceAccess, reportAnchor])

  useEffect(() => {
    sendReportContext()
  }, [sendReportContext])

  useEffect(() => {
    if (!publicCopy && !publicProspect && loading) return
    setConfig(null)
    setError(null)

    if (publicProspect) {
      setConfig({ slug, name: 'Yan-Vada Partnership Prospectus' } as SponsorConfig)
      return
    }

    if (publicCopy) {
      fetch(`/api/temporary/sponsors/${slug}`, { credentials: 'include' })
        .then(async (r) => {
          const contentType = r.headers.get('content-type') ?? ''
          if (!contentType.includes('json')) throw new Error('not-found')
          if (!r.ok) throw new Error('not-found')
          return r.json() as Promise<SponsorConfig>
        })
        .then((data) => { setConfig(data) })
        .catch(() => { setError('not-found') })
      return
    }

    if (isMasterReport) {
      if (!isLoggedIn) {
        setError('auth_required')
        return
      }
      if (status !== 'approved') {
        setError('sponsor_access_required')
        return
      }
      if (!bypassesReportNda && !hasAcceptedReportNda) {
        setError('nda_required')
        return
      }
      setConfig({ slug, name: REPORT_FALLBACK_NAMES[slug] } as SponsorConfig)
      return
    }

    fetch(`/api/sponsors/${slug}`, { credentials: 'include' })
      .then(async (r) => {
        const contentType = r.headers.get('content-type') ?? ''
        if (!contentType.includes('json')) throw new Error('not-found')
        if (!r.ok) {
          const data = await r.json().catch(() => ({})) as { error?: string }
          throw new Error(data.error === 'not_found' ? 'not-found' : (data.error ?? 'not-found'))
        }
        return r.json() as Promise<SponsorConfig>
      })
      .then((data) => { setConfig(data) })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'not-found'
        setError(message === 'not_found' ? 'not-found' : message)
      })
  }, [slug, isMasterReport, isLoggedIn, status, bypassesReportNda, hasAcceptedReportNda, ndaAcceptanceKey, loading, publicCopy, publicProspect])

  if (error === 'not-found') return <NotFound />
  if (error === 'auth_required') {
    return (
      <main className="sponsor-page sponsor-page-state">
        <div className="sponsor-content sponsor-content-state">
          <section className="section-text">
            <h1>Sign in to view this value report.</h1>
            <p>This report is intended for approved Yan-Vada partners and team members.</p>
            <Link to={`/signin?next=${encodeURIComponent(`${location.pathname}${location.search}${location.hash}`)}`} className="btn btn-primary">Sign in</Link>
          </section>
        </div>
      </main>
    )
  }
  if (error === 'sponsor_access_required') {
    return (
      <main className="sponsor-page sponsor-page-state">
        <div className="sponsor-content sponsor-content-state">
          <section className="section-text">
            <h1>Access is pending.</h1>
            <p>Your account is signed in, but this sponsor report has not been approved for your email yet.</p>
            <Link to="/request-access" className="btn btn-primary">Request access</Link>
          </section>
        </div>
      </main>
    )
  }
  if (error === 'nda_required') {
    return (
      <main className="sponsor-page sponsor-page-state">
        <div className="sponsor-content sponsor-content-state">
          <section className="section-text">
            <h1>NDA acceptance required.</h1>
            <p>Accept the confidentiality gate to unlock this sponsor value report.</p>
          </section>
          <NDAGate sponsorSlug={slug} />
        </div>
      </main>
    )
  }
  if (error) return <div className="error">{error}</div>
  if (!config) return <div className="loading">Loading...</div>

  return (
    <main className="sponsor-report-page" data-surface="branding" data-sponsor={config.slug}>
      <iframe ref={frameRef} title={`${config.name} sponsor value report`} src={reportSrc} onLoad={sendReportContext} />
    </main>
  )
}
